import { useMemo } from 'react';
import {
  DEFAULT_COUNTRY,
  DEFAULT_CURRENCY,
  DEFAULT_LOCALE,
  DEFAULT_TIMEZONE,
  formatTaxRate,
  workspace as wsRules,
} from '@buildbee/domain';
import { formatDate, formatDateTime, formatMoney } from '@buildbee/ui';
import { trpc } from './trpc';

/**
 * Regional settings of a workspace (currency, locale, time zone, country, tax province) with the
 * Canadian defaults BuildBee falls back to: CAD, en-CA, America/Toronto, Canada.
 */
export type RegionalInput =
  | {
      currency?: string | null;
      locale?: string | null;
      timezone?: string | null;
      country?: string | null;
      taxRegion?: string | null;
    }
  | null
  | undefined;

export type Regional = { currency: string; locale: string; timezone: string; country: string; taxRegion: string | null };

export function regionalSettings(s: RegionalInput): Regional {
  return {
    currency: s?.currency || DEFAULT_CURRENCY,
    locale: s?.locale || DEFAULT_LOCALE,
    timezone: s?.timezone || DEFAULT_TIMEZONE,
    country: s?.country || DEFAULT_COUNTRY,
    taxRegion: s?.taxRegion || null,
  };
}

/** Workspace currency/locale/time zone/country for formatting, with Canadian defaults while loading. */
export function useRegional() {
  const current = trpc.workspace.current.useQuery(undefined, { staleTime: 5 * 60_000 });
  const settings = current.data?.workspace.settings;
  return useMemo(() => {
    const r = regionalSettings(settings);
    return {
      ...r,
      loading: current.isLoading,
      money: (cents: number | null | undefined) => formatMoney(cents, r.currency, r.locale),
      date: (value: Date | string | null | undefined, opts?: Intl.DateTimeFormatOptions) => formatDate(value, r.locale, opts),
      dateTime: (value: Date | string | null | undefined) => formatDateTime(value, r.locale),
    };
  }, [settings, current.isLoading]);
}

/** Sales tax components (GST/HST/PST/RST/QST) of the workspace's province; null outside Canada or without a province. */
export function provincialTax(s: RegionalInput, onDate?: string) {
  const r = regionalSettings(s);
  return wsRules.workspaceTaxComponents({ country: r.country.toUpperCase(), taxRegion: r.taxRegion?.toUpperCase() ?? null }, onDate);
}

/**
 * Label for a document's single tax line. When the document's rate is the workspace province's
 * combined rate it names the components ("HST 13%", "GST 5% + PST 7%"); otherwise "Tax 8%".
 * Quebec's 14.975% is stored as 1498 bps on documents, so the comparison rounds the total.
 */
export function taxLineLabel(rateBps: number, s: RegionalInput, onDate?: string): string {
  const tax = provincialTax(s, onDate);
  if (tax && rateBps > 0 && Math.round(tax.totalRateBps) === rateBps) return tax.components.map((c) => c.label).join(' + ');
  return `Tax ${formatTaxRate(rateBps)}`;
}

/** Currencies a workspace can bill in. */
export const CURRENCY_OPTIONS = [
  { value: 'CAD', label: 'CAD: Canadian dollar' },
  { value: 'USD', label: 'USD: US dollar' },
] as const;

export const LOCALE_OPTIONS = [
  { value: 'en-CA', label: 'English (Canada)' },
  { value: 'fr-CA', label: 'Français (Canada)' },
  { value: 'en-US', label: 'English (United States)' },
] as const;

/** Canadian then US time zones (IANA names). */
export const TIMEZONE_OPTIONS = [
  { value: 'America/St_Johns', label: 'Newfoundland (St. John’s)' },
  { value: 'America/Halifax', label: 'Atlantic (Halifax)' },
  { value: 'America/Moncton', label: 'Atlantic (Moncton)' },
  { value: 'America/Toronto', label: 'Eastern (Toronto)' },
  { value: 'America/Winnipeg', label: 'Central (Winnipeg)' },
  { value: 'America/Regina', label: 'Saskatchewan (Regina)' },
  { value: 'America/Edmonton', label: 'Mountain (Edmonton)' },
  { value: 'America/Vancouver', label: 'Pacific (Vancouver)' },
  { value: 'America/Whitehorse', label: 'Yukon (Whitehorse)' },
  { value: 'America/New_York', label: 'US Eastern (New York)' },
  { value: 'America/Chicago', label: 'US Central (Chicago)' },
  { value: 'America/Denver', label: 'US Mountain (Denver)' },
  { value: 'America/Phoenix', label: 'US Arizona (Phoenix)' },
  { value: 'America/Los_Angeles', label: 'US Pacific (Los Angeles)' },
  { value: 'America/Anchorage', label: 'Alaska (Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (Honolulu)' },
] as const;
